import { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import { Spinner } from '@components';
import { STORAGE_KEYS } from '@constants/storage';
import {
  useLazyAppConfigQuery,
  useLazyGetOrganizationListQuery
} from '@features/authentication/queries/AuthQuery';
import { useLazyGetOrganizationConfigQuery } from '@features/group-management/queries/GroupQuery';
import {
  setAccountType,
  setAppConfig,
  setCurrentOrg,
  setOrgConfig
} from '@layouts/main-header/userSlice';
import { getLocalStorageItem } from '@utils/storage';
import { Org } from 'types/organization';

import AccessGuard from '../access-guard/AccessGuard';
import ApprovalGuard from '../approval-guard/ApprovalGuard';
import MobileLayout from '../mobile-layout/MobileLayout';

const BlankLayout = () => {
  const dispatch = useDispatch();
  const token = getLocalStorageItem(STORAGE_KEYS.ACCESS_TOKEN);
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const { currentOrg, orgConfig, appConfig } = useSelector(
    (state: any) => state.user
  );
  const [getOrgList] = useLazyGetOrganizationListQuery();
  const [getOrgConfig] = useLazyGetOrganizationConfigQuery();
  const [getAppConfigQuery] = useLazyAppConfigQuery();

  useEffect(() => {
    if (!token || currentOrg?.id) return;

    const fetchOrgDetails = async () => {
      const resp = await getOrgList(null, false);
      if ('data' in resp) {
        const org = resp.data?.find((o: Org) => o.id === organizationId);
        if (org) {
          dispatch(setCurrentOrg(org));
          dispatch(setAccountType({ type: org?.type }));
        }
      }
    };

    fetchOrgDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organizationId]);

  useEffect(() => {
    if (!token || !organizationId) return;

    if (!orgConfig) {
      const fetchOrgConfig = async () => {
        const resp = await getOrgConfig({ organizationId });
        if ('data' in resp) {
          dispatch(setOrgConfig(resp.data));
        }
      };
      fetchOrgConfig();
    }

    if (!appConfig) {
      const fetchAppConfig = async () => {
        const resp = await getAppConfigQuery(null);
        if ('data' in resp) {
          dispatch(setAppConfig(resp.data));
        }
      };
      fetchAppConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organizationId]);

  return (
    <AccessGuard>
      <div className="z-100 hidden h-screen w-screen overflow-hidden mobile:flex">
        <MobileLayout />
      </div>
      <div className="flex h-screen w-screen overflow-hidden mobile:hidden">
        <Suspense
          fallback={
            <div className="absolute left-1/2 top-1/2 flex size-full -translate-x-1/2 -translate-y-1/2 items-center justify-center">
              <Spinner />
            </div>
          }
        >
          <ApprovalGuard>
            <Outlet />
          </ApprovalGuard>
        </Suspense>
      </div>
    </AccessGuard>
  );
};

export default BlankLayout;
